import React from 'react';

import { CHART_BAR, GRID } from '../icons';
import { CHART_SCOPE } from '../../services/charts';

/**
 * A saved chart's actions, in the platform taxonomy (standards §3; plan
 * §6.1): the Reports row's ⋯ and the chart page's ⋯ are built from this one
 * list, so a chart offers the same verbs wherever it is (the availability
 * rule). A surface passes only the handlers that make sense where it is: the
 * row has Open, the page has Open the list and Go to Reports instead, and the
 * page's Edit is its primary button, not a menu entry.
 *
 *   Go to            Open · Open the list · Go to Reports
 *   Record           Edit… · Rename… · Duplicate… · Add to dashboard… · Pin to Home ·
 *                    Pin for everyone | Unpin — return to your own list
 *   Share & export   Download image · Download data · Copy link
 *   Danger           Delete…
 *
 * Who may do what is the server's answer: `chart.canEdit` (its creator for a
 * personal chart, a manager for one pinned for everyone) gates Edit, Rename
 * and Delete; pinning and unpinning are a manager's,
 * on this collection's own charts. Duplicate makes a personal copy, which
 * anyone who reads the chart may; so is Add to dashboard… (charts A6), which
 * places it on a dashboard the person may arrange, and Pin to Home (charts
 * A7, ruled d18): the person's own placement of the chart on Home, in THIS
 * collection (the context it counts), with the `pin` glyph — never the
 * collection tier's "Pin for everyone".
 *
 * Share & export (A9, useChartShare): anyone who reads the chart may download
 * it and copy its link, as anyone who reads a list may export it (the item
 * list's Export view…, CollectionActions) — the server narrows Download data
 * to what they may export, and says so when they may not export at all. A
 * table has no picture, so it is not offered Download image; a surface passes
 * `imageReason` / `dataReason` to grey an entry out with its one-line reason
 * (the chart has not run, a download is already under way).
 */
export function chartActions({
	chart,
	isManager = false,
	onOpen,
	onOpenList,
	openListReason = null,
	onReports,
	onEdit,
	onRename,
	onDuplicate,
	onAddToDashboard,
	pinnedHome = false,
	onPinHome,
	onPromote,
	onDemote,
	onDownloadImage,
	imageReason = null,
	onDownloadData,
	dataReason = null,
	onCopyLink,
	linkReason = null,
	onDelete,
}) {
	if (!chart) return [];
	const forEveryone = chart.scope === CHART_SCOPE.COLLECTION;
	const mayEdit = chart.canEdit === true;
	const drawnAsTable = Boolean(chart.spec && chart.spec.show && chart.spec.show.type === 'table');
	return [
		onOpen ? { key: 'open', group: 'navigate', glyph: 'open', label: 'Open', onClick: onOpen } : null,
		onOpenList ? {
			key: 'open-list',
			group: 'navigate',
			glyph: 'items',
			label: 'Open the list',
			// No exact list behind the whole chart: said, never opened wrong (d28).
			disabled: Boolean(openListReason),
			title: openListReason || undefined,
			onClick: onOpenList,
		} : null,
		onReports ? { key: 'reports', group: 'navigate', icon: <CHART_BAR />, label: 'Go to Reports', onClick: onReports } : null,
		onEdit && mayEdit ? { key: 'edit', group: 'record', glyph: 'edit', label: 'Edit…', onClick: onEdit } : null,
		onRename && mayEdit ? { key: 'rename', group: 'record', glyph: 'edit', label: 'Rename…', onClick: onRename } : null,
		onDuplicate ? { key: 'duplicate', group: 'record', glyph: 'duplicate', label: 'Duplicate…', onClick: onDuplicate } : null,
		onAddToDashboard ? { key: 'add-to-dashboard', group: 'record', icon: <GRID />, label: 'Add to dashboard…', onClick: onAddToDashboard } : null,
		onPinHome ? { key: 'pin-home', group: 'record', glyph: 'pin', label: pinnedHome ? 'Unpin from Home' : 'Pin to Home', onClick: onPinHome } : null,
		onPromote && isManager && !forEveryone ? {
			key: 'promote', group: 'record', glyph: 'collection', label: 'Pin for everyone', onClick: onPromote,
		} : null,
		onDemote && isManager && forEveryone ? {
			key: 'demote', group: 'record', glyph: 'collection', label: 'Unpin — return to your own list', onClick: onDemote,
		} : null,
		onDownloadImage && !drawnAsTable ? {
			key: 'download-image', group: 'share', glyph: 'export', label: 'Download image',
			disabled: Boolean(imageReason), title: imageReason || undefined, onClick: onDownloadImage,
		} : null,
		onDownloadData ? {
			key: 'download-data', group: 'share', glyph: 'export', label: 'Download data',
			disabled: Boolean(dataReason), title: dataReason || undefined, onClick: onDownloadData,
		} : null,
		onCopyLink ? {
			key: 'copy-link', group: 'share', glyph: 'share', label: 'Copy link',
			disabled: Boolean(linkReason), title: linkReason || undefined, onClick: onCopyLink,
		} : null,
		onDelete && mayEdit ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete…', onClick: onDelete } : null,
	].filter(Boolean);
}

/** The words of a chart's delete confirmation: what goes, for whom, and that History brings it back. */
export function deleteChartCopy(chart) {
	const name = chart && typeof chart.name === 'string' && chart.name ? chart.name : 'this chart';
	return {
		title: `Delete "${name}"?`,
		content: chart && chart.scope === CHART_SCOPE.COLLECTION
			? 'It disappears for everyone who uses this collection. History can restore it.'
			: 'Only you can see this chart, so only you lose it. History can restore it.',
	};
}
